/* eslint-disable @atlaskit/design-system/use-primitives-text */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import gridBackground from '../../../../../assets/grid-background.svg';
import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import { useAtlassianUserContext } from '../../../../../hooks/use-user-context';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { CollaborationUsersAtom } from '../../../../state';
import { SpacingWrapper } from '../../../SpacingWrapper';
import { STATIC_COLLABORATORS_DATA } from '../static-data';

import blueBackground from './assets/blue-background.webp';
import blueMask from './assets/blue-mask.svg';
import greyBackground from './assets/grey-background.webp';
import greyMask from './assets/grey-mask.svg';
import limeBackground from './assets/lime-background.webp';
import limeMask from './assets/lime-mask.svg';
import purpleBackground from './assets/purple-background.webp';
import purpleMask from './assets/purple-mask.svg';
import yellowBackground from './assets/yellow-background.webp';
import yellowMask from './assets/yellow-mask.svg';
import { messages } from './messages';

const RANK_CARDS = [
	{ backgroundColor: '#82B536', backgroundSrc: limeBackground, maskSrc: limeMask },
	{ backgroundColor: '#BF63F3', backgroundSrc: purpleBackground, maskSrc: purpleMask },
	{ backgroundColor: '#4D8CED', backgroundSrc: blueBackground, maskSrc: blueMask },
	{ backgroundColor: '#FCA700', backgroundSrc: yellowBackground, maskSrc: yellowMask },
	{ backgroundColor: '#8C8F97', backgroundSrc: greyBackground, maskSrc: greyMask },
] as const;

const styles = cssMap({
	section: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
		backgroundPositionX: '0',
		backgroundPositionY: '0',
		'@media (max-width: 30rem)': {
			backgroundPositionX: '-30px',
			backgroundPositionY: '0',
		},
	},
	contentWrapper: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		gap: '3.75rem',
	},
	statsRow: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'center',
		gap: token('space.100'),
		paddingInline: token('space.400'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		height: '360px',
	},
	bigNumber: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: 'InterVariable, sans-serif',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '160px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 300,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '100%',
		fontVariantNumeric: 'lining-nums proportional-nums',
		fontFeatureSettings: "'ss01' on, 'ss02' on, 'ss07' on, 'cv11' on",
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '3px',
		margin: 0,
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	bigNumberMobile: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		fontFamily: token('font.family.brand.heading'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '80px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '3px',
		margin: 0,
		display: 'none',
		'@media (max-width: 30rem)': {
			display: 'unset',
		},
	},
	bigNumberLabel: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.heading.large'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '150%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		margin: 0,
	},
	description: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: token('color.text.subtlest'),
		font: token('font.body.large'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		margin: 0,
		width: '100%',
		maxWidth: '800px',
	},
	collaboratorsList: {
		display: 'flex',
		flexDirection: 'column',
		bacgkgroundColor: 'transparent',
	},
	collaboratorRowContainer: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '1px',
		height: '120px',
		width: '100%',
	},
	collaboratorRow: {
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	avatarContainer: {
		width: '118px',
		aspectRatio: '1 / 1',
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderRight: '1px dashed #505258',
		flexShrink: 0,
	},
	avatarWrapper: {
		width: '100%',
		height: '100%',
		position: 'relative',
		borderRadius: token('radius.xsmall'),
		overflow: 'hidden',
	},
	avatarBackground: {
		position: 'absolute',
		inset: 0,
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		display: 'block',
	},
	avatar: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		flexShrink: 0,
		borderRadius: token('radius.xsmall'),
	},
	collaboratorHeaderContainer: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '1px',
		width: '100%',
		height: '120px',
	},
	collaboratorHeader: {
		paddingInline: token('space.400'),
		height: '100%',
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
	collaboratorHeaderLabel: {
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '150%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
	},
	collaboratorInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		paddingInline: token('space.400'),
		flex: '1 1 auto',
	},
	collaboratorName: {
		color: token('color.text'),
		font: token('font.body.large'),
		margin: 0,
	},
	collaboratorSharedItems: {
		color: token('color.text.subtlest'),
		font: token('font.body.large'),
		margin: 0,
	},
	collaboratorScoreWrapper: {
		textAlign: 'right',
		paddingRight: token('space.400'),
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	collaboratorScoreLabel: {
		font: token('font.body'),
		fontFamily: token('font.family.code'),
		color: token('color.text.subtlest'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
	collaboratorScore: {
		font: token('font.body'),
		textAlign: 'right',
		margin: 0,
	},
});

type CollaboratorsSectionProps = {
	metrics: OrgMetricsResponse;
};

export const CollaboratorsSection = (_: CollaboratorsSectionProps): JSX.Element | null => {
	const { formatNumber } = useIntl();
	const { data: collaborators, error } = useAtomValue(CollaborationUsersAtom);

	const { totalCollaborators } = STATIC_COLLABORATORS_DATA;
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.Collaborators);
	const { identity: currentUser } = useAtlassianUserContext();

	// TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback
	if (error !== null) {
		return (
			<div>
				<FormattedMessage {...messages.loadCollaboratorsError} />
			</div>
		);
	}

	const rankedCollaborators = collaborators
		.filter((user) => user.id !== currentUser.id)
		.slice(0, RANK_CARDS.length);

	// TODO: GRAPHYTE-2516: no collaborators to show
	if (rankedCollaborators.length <= 2) {
		// eslint-disable-next-line no-console
		console.log('less than 3 collaborators to show');
		return null;
	}

	return (
		<section
			ref={sectionRef}
			css={styles.section}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{ backgroundImage: `url(${gridBackground})` }}
		>
			<SpacingWrapper>
				<div css={styles.contentWrapper}>
					<div css={styles.content}>
						<div css={styles.statsRow}>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.bigNumberLabel}>
								<FormattedMessage {...messages.totalCollaboratorsLabel} />
							</p>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.bigNumber}>{formatNumber(totalCollaborators)}</p>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.bigNumberMobile}>{formatNumber(totalCollaborators)}</p>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.description}>
								<FormattedMessage {...messages.totalCollaboratorsDescription} />
							</p>
						</div>
						<div css={styles.collaboratorsList}>
							<div css={styles.collaboratorHeaderContainer}>
								<div css={styles.collaboratorHeader}>
									{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
									<p css={styles.collaboratorHeaderLabel}>
										<FormattedMessage {...messages.collaboratorHeaderLabel} />
									</p>
								</div>
							</div>

							{collaborators.map((collaborator, index) => (
								<div key={collaborator.expand.name} css={styles.collaboratorRowContainer}>
									<div css={styles.collaboratorRow}>
										<div css={styles.avatarContainer}>
											<div css={styles.avatarWrapper}>
												<img
													css={styles.avatarBackground}
													src={RANK_CARDS[index].backgroundSrc}
													alt=""
													aria-hidden="true"
												/>
												<img
													css={styles.avatar}
													src={collaborator.expand.picture?.replace(/\/128$/, '/512')}
													alt={collaborator.expand.name}
													style={{
														maskImage: `url(${RANK_CARDS[index].maskSrc})`,
														WebkitMaskImage: `url(${RANK_CARDS[index].maskSrc})`,
													}}
												/>
											</div>
										</div>
										<div css={styles.collaboratorInfo}>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<p css={styles.collaboratorName}>{collaborator.expand.name}</p>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<p css={styles.collaboratorSharedItems}>
												<FormattedMessage
													{...messages.sharedItemsLabel}
													values={{
														count: 1234, // not available yet
													}}
												/>
											</p>
										</div>
										<div css={styles.collaboratorScoreWrapper}>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<p css={styles.collaboratorScoreLabel}>
												<FormattedMessage {...messages.collaborationScoreLabel} />
											</p>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<p css={styles.collaboratorScore}>{collaborator.score}</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</SpacingWrapper>
		</section>
	);
};
